import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Ban, Briefcase, Check, Gavel, Pause, Pencil, Play, RotateCcw, Send, UserPlus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  HIRE_KIND_LABELS,
  REQUISITION_PRIORITY_LABELS,
  SLA_STATE_LABELS,
  formatMoney,
  jevBudgetLabel,
  jevBudgetRequestLineOf,
  jevBudgetSettledOf,
  type ApprovalStepView,
  type RequisitionDetail,
  type SlaSummary,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Popover, usePopover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import {
  ForbiddenState,
  NotFoundState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDate, formatDateTime, orEmpty } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanEdit, useCanView } from '../../../stores/session';
import { CustomFieldsCard } from '../../admin/components/CustomFieldsCard';
import { useToasts } from '../../../stores/toasts';
import { useUsers } from '../../admin/api/users-api';
import { SalaryBand } from '../../organisation/components/SalaryBand';
import {
  useAssignRecruiter,
  useRequisition,
  useRequisitionAction,
  type LifecycleAction,
} from '../api/requisitions-api';
import { DecisionDialog } from '../components/DecisionDialog';
import { RequestContentCards } from '../components/RequestContent';
import { ReasonDialog } from '../components/ReasonDialog';
import {
  APPROVAL_STEP_LABELS,
  PriorityDot,
  RequisitionStatusChip,
  SlaFigure,
} from '../components/RequisitionBadges';
import { formatActive, formatElapsed } from '../sla-format';
import styles from './RequisitionDetailPage.module.css';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';

/**
 * REQ-03 — one requisition's whole record (E2): requester, what they wrote for the job (ADR 0075), the
 * approval trail, the linked position, its clocks, and every version.
 *
 * Workflow review 2026-10-29 (mockup screen 3): the maker → checker → approver trail sits
 * beside the request, and once approved the draft job made from it opens from the header.
 *
 * The buttons shown are the ones the server says this viewer may use (`actions`);
 * the server checks each again when it is pressed, so a stale screen can never do
 * more than the viewer is allowed.
 */

export interface RequisitionDetailSearch {
  /** REQ-05 by deep link: N-01 and N-02 land here with the step to decide. */
  readonly decide?: string;
}

type Prompt = 'hold' | 'cancel' | null;

export function RequisitionDetailPage(): ReactNode {
  const canView = useCanView('requisition');
  const navigate = useNavigate();
  const { requisitionId } = useParams({ from: '/app-layout/requisitions/$requisitionId' });
  const search = useSearch({ from: '/app-layout/requisitions/$requisitionId' }) as RequisitionDetailSearch;
  const requisition = useRequisition(canView ? requisitionId : null);
  useRecordVisit(requisition.data ? { kind: 'requisition', id: requisitionId, title: requisition.data.title, subtitle: requisition.data.reference, path: `/requisitions/${requisitionId}` } : null, goneId(requisition.error, requisitionId));

  if (!canView) return <ForbiddenState resource="this requisition" contact="Your admin" />;
  if (requisition.isError && requisition.error.status === 403) {
    return <ForbiddenState resource="this requisition" contact="Your admin" />;
  }
  if (requisition.isError) {
    return requisition.error.status === 404 ? (
      <NotFoundState onGoBack={() => void navigate({ to: '/requisitions' })} />
    ) : (
      <ServerErrorState onRetry={() => void requisition.refetch()} />
    );
  }
  if (requisition.isPending || !requisition.data) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={8} />
      </div>
    );
  }
  return (
    <RequisitionRecord
      detail={requisition.data}
      decideStepId={search.decide ?? null}
      onCloseDecision={() =>
        void navigate({
          to: '/requisitions/$requisitionId',
          params: { requisitionId },
          search: {},
          replace: true,
        })
      }
    />
  );
}

function RequisitionRecord({
  detail,
  decideStepId,
  onCloseDecision,
}: {
  readonly detail: RequisitionDetail;
  readonly decideStepId: string | null;
  readonly onCloseDecision: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const showToast = useToasts((state) => state.show);
  const act = useRequisitionAction();
  const [prompt, setPrompt] = useState<Prompt>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const canEditRequisition = useCanEdit('requisition');

  const deciding =
    detail.approvals.find((step) => step.id === (decidingId ?? decideStepId) && step.canDecide) ?? null;
  const has = (action: RequisitionDetail['actions'][number]): boolean => detail.actions.includes(action);

  async function run(action: LifecycleAction, reason?: string): Promise<void> {
    try {
      await act.mutateAsync({ id: detail.id, action, version: detail.version, ...(reason === undefined ? {} : { reason }) });
      setPrompt(null);
      showToast({ tone: 'success', message: DONE[action](detail.reference) });
      if (action === 'revise') {
        await navigate({ to: '/requisitions/$requisitionId/edit', params: { requisitionId: detail.id } });
      }
    } catch (cause) {
      showToast({ tone: 'danger', message: 'That was not done', detail: errorMessage(cause) });
    }
  }

  const myStep = detail.approvals.find((step) => step.canDecide) ?? null;

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/requisitions" className={styles.crumb}>
          Requisitions
        </Link>
        <span aria-hidden>/</span>
        <span>{detail.reference}</span>
      </nav>

      <header className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.code}>
            {detail.reference}
            {detail.currentVersion > 1 ? ` · version ${detail.currentVersion}` : ''}
          </span>
          <h1 className={styles.title}>{detail.title || 'Untitled draft'}</h1>
          <p className={styles.unit}>{summaryLine(detail)}</p>
        </div>
        <div className={styles.headStatus}>
          <RequisitionStatusChip status={detail.status} />
          <PriorityDot priority={detail.priority} />
          {detail.statusReason !== null && <p className={styles.reason}>“{detail.statusReason}”</p>}
        </div>
      </header>

      <div className={styles.actions}>
        {detail.job && (
          <Button variant="solid" icon={Briefcase} onClick={() => void navigate({ to: '/jobs/$jobId', params: { jobId: detail.job?.id ?? '' } })}>
            Open the job
          </Button>
        )}
        {myStep && (
          <Button variant="solid" icon={Gavel} onClick={() => setDecidingId(myStep.id)}>
            Decide
          </Button>
        )}
        {has('submit') && (
          <Button variant="solid" icon={Send} isPending={act.isPending} onClick={() => void run('submit')}>
            Submit for approval
          </Button>
        )}
        {has('edit') && (
          <Button
            variant="outline"
            icon={Pencil}
            onClick={() => void navigate({ to: '/requisitions/$requisitionId/edit', params: { requisitionId: detail.id } })}
          >
            {detail.isAtHrCheck ? 'Check and edit' : 'Edit'}
          </Button>
        )}
        {has('revise') && (
          <Button variant="solid" icon={RotateCcw} isPending={act.isPending} onClick={() => void run('revise')}>
            Revise as version {detail.currentVersion + 1}
          </Button>
        )}
        {has('hold') && (
          <Button variant="outline" icon={Pause} onClick={() => setPrompt('hold')}>
            Put on hold
          </Button>
        )}
        {has('resume') && (
          <Button variant="outline" icon={Play} isPending={act.isPending} onClick={() => void run('resume')}>
            Resume
          </Button>
        )}
        {has('assign_recruiter') && (
          <RecruiterButton detail={detail} />
        )}
        {has('cancel') && (
          <Button variant="ghost" icon={Ban} onClick={() => setPrompt('cancel')}>
            Cancel requisition
          </Button>
        )}
      </div>

      <div className={styles.pair}>
        <Card title="The request">
          <dl className={styles.facts}>
            <Fact label="Position" value={detail.position ? `${detail.position.title} · ${detail.departmentName} (existing position)` : `${detail.departmentName} (new position)`} />
            <Fact label="Type" value={typeOf(detail)} />
            <Fact label="How many" value={String(detail.slotsRequested)} />
            <Fact label="Start" value={startOf(detail)} />
            <Fact label="Budget" value={budgetOf(detail)} />
            <Fact label="How urgent" value={REQUISITION_PRIORITY_LABELS[detail.priority]} />
            <Fact label="Jev budget" value={jevBudgetOf(detail)} />
            <Fact label="Hiring manager" value={orEmpty(detail.hiringManagerName)} />
            <Fact label="Recruiter" value={orEmpty(detail.recruiterName)} />
          </dl>
          {detail.job && (
            <p className={styles.notice} role="status">
              <Check size={16} aria-hidden />
              <span>
                <b>Draft job {detail.job.reference} made</b> on {formatDateTime(detail.job.createdAt)} from this request.
                {detail.recruiterName ? ` Recruiter ${detail.recruiterName} was told.` : ''}
              </span>
            </p>
          )}
        </Card>

        <Card title="Approval trail">
          <ApprovalTrail detail={detail} onDecide={(step) => setDecidingId(step.id)} />
        </Card>
      </div>

      {/* ADR 0075: what the requester wrote for the job, HR's marks, and the list of changes. */}
      <RequestContentCards detail={detail} />

      <div className={styles.grid}>
        <ClocksCard detail={detail} />

        <Card title="Position">
          {detail.position ? (
            <dl className={styles.facts}>
              <Fact
                label="Seat"
                value={
                  <Link
                    to="/organisation/positions/$positionId"
                    params={{ positionId: detail.position.id }}
                    className={styles.link}
                  >
                    {detail.position.code}
                  </Link>
                }
              />
              <Fact label="Title" value={detail.position.title} />
              <Fact label="Open seats" value={String(detail.position.openSlots)} />
              {/* §L23: masked on the server per record; this only renders what arrived. */}
              <Fact label="Salary band" value={<SalaryBand position={detail.position} />} />
            </dl>
          ) : (
            <p className={styles.meta}>A new position. It is created when this requisition is approved.</p>
          )}
        </Card>
      </div>

      <CustomFieldsCard module="requisition" entityId={detail.id} canEdit={canEditRequisition} />

      {detail.versions.length > 0 && (
        <Card title="Versions" meta="Each version is kept as it was submitted">
          <ul className={styles.versions}>
            {detail.versions.map((version) => (
              <li key={version.versionNumber} className={styles.version}>
                <details>
                  <summary className={styles.versionSummary}>
                    <span className={styles.code}>Version {version.versionNumber}</span>
                    <span className={styles.meta}>
                      {formatDateTime(version.createdAt)}
                      {version.createdByName ? ` · ${version.createdByName}` : ''}
                    </span>
                    {version.changeReason && <span className={styles.reasonInline}>{version.changeReason}</span>}
                  </summary>
                  <dl className={styles.facts}>
                    {Object.entries(version.snapshot).map(([key, value]) => (
                      <Fact key={key} label={SNAPSHOT_LABELS[key] ?? key} value={snapshotValueOf(key, value)} />
                    ))}
                  </dl>
                </details>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {deciding && (
        <DecisionDialog
          stepId={deciding.id}
          stepVersion={deciding.version}
          reference={detail.reference}
          title={deciding.name}
          onBehalfOfName={null}
          jevBudgetMicroUsd={jevBudgetSettledOf(detail)}
          onClose={() => {
            setDecidingId(null);
            if (decideStepId) onCloseDecision();
          }}
        />
      )}
      {prompt === 'hold' && (
        <ReasonDialog
          title={`Put ${detail.reference} on hold`}
          description="Its clock pauses until it is resumed. The requester and recruiter are told why."
          label="Why is it on hold?"
          confirmLabel="Put on hold"
          isPending={act.isPending}
          onConfirm={(reason) => void run('hold', reason)}
          onClose={() => setPrompt(null)}
        />
      )}
      {prompt === 'cancel' && (
        <ReasonDialog
          title={`Cancel ${detail.reference}`}
          description="A cancelled requisition stops every clock and frees its seat. Its job is cancelled too, and everyone still in it is closed. It can only be reopened by HR."
          label="Why is it cancelled?"
          confirmLabel="Cancel requisition"
          isDanger
          isReasonOptional={detail.status === 'draft'}
          isPending={act.isPending}
          onConfirm={(reason) => void run('cancel', reason === '' ? undefined : reason)}
          onClose={() => setPrompt(null)}
        />
      )}
    </div>
  );
}

/**
 * Maker → checker → approver (workflow review 2026-10-29): who asked, each approval step
 * with its role and who decided, and the job made at the end. Earlier rounds (a request
 * sent back and sent again) fold under the current one.
 */
function ApprovalTrail({
  detail,
  onDecide,
}: {
  readonly detail: RequisitionDetail;
  readonly onDecide: (step: ApprovalStepView) => void;
}): ReactNode {
  const steps = detail.approvals;
  const current = steps.filter((step) => (step.requisitionVersion ?? 1) === detail.currentVersion);
  const earlier = steps.filter((step) => (step.requisitionVersion ?? 1) !== detail.currentVersion);
  const numbers = [...new Set(current.map((step) => step.stepNumber))].sort((a, b) => a - b);
  const isSent = detail.submittedAt !== null;
  return (
    <div className={styles.trailWrap}>
      <ol className={styles.trail}>
        <TrailItem
          state={isSent ? 'done' : 'off'}
          role="Maker"
          title={`${detail.requestedByName ?? 'Someone'} ${isSent ? 'asked' : 'is writing it'}`}
          when={detail.submittedAt}
        />
        {numbers.map((number, index) => {
          const group = current.filter((step) => step.stepNumber === number);
          const first = group[0];
          if (!first) return null;
          const isCurrent = group.some((step) => step.status === 'pending' && step.activatedAt !== null);
          const decided = group.find((step) => step.actedByName !== null);
          const state = group.every((step) => step.status === 'approved') ? 'done' : isCurrent ? 'wait' : group.some((step) => step.status === 'rejected' || step.status === 'sent_back') ? 'stop' : 'off';
          // A role step reads "HR — Engineering"; the trail needs only the role.
          const who = first.isRoleStep ? (first.approverName.split(' — ')[0] ?? first.approverName) : first.approverName;
          const roleLabel = index === 0 ? `Checker · ${who}` : `Approver · ${who}`;
          const title = decided
            ? `${decided.actedByName}${decided.onBehalfOfName ? ` for ${decided.onBehalfOfName}` : ''} ${STEP_VERB[decided.status] ?? 'acted'}`
            : isCurrent
              ? `Waiting on ${first.name.toLowerCase()}`
              : first.name;
          const decidable = group.find((step) => step.canDecide);
          return (
            <TrailItem
              key={number}
              state={state}
              role={roleLabel}
              title={title}
              when={decided?.decidedAt ?? (isCurrent ? first.activatedAt : null)}
              quote={decided?.decisionReason ?? null}
              action={
                decidable ? (
                  <Button size="sm" variant="outline" icon={Gavel} onClick={() => onDecide(decidable)}>
                    Decide
                  </Button>
                ) : null
              }
              sla={isCurrent ? first.sla : null}
            />
          );
        })}
        {numbers.length === 0 && isSent && <TrailItem state="wait" role="Approval" title="Being routed to its approvers" when={null} />}
        <TrailItem
          state={detail.job ? 'done' : 'off'}
          role="Recruitment"
          title={detail.job ? `Draft job ${detail.job.reference} made` : 'The draft job is made when it is approved'}
          when={detail.job?.createdAt ?? null}
        />
      </ol>
      {earlier.length > 0 && (
        <details className={styles.earlier}>
          <summary>Earlier versions</summary>
          <ul className={styles.earlierList}>
            {earlier.map((step) => (
              <li key={step.id}>
                Version {step.requisitionVersion ?? 1} · {step.name}: {APPROVAL_STEP_LABELS[step.status]}
                {step.actedByName ? ` by ${step.actedByName}` : ''}
                {step.decisionReason ? ` — “${step.decisionReason}”` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const STEP_VERB: Readonly<Partial<Record<ApprovalStepView['status'], string>>> = {
  approved: 'approved',
  rejected: 'rejected it',
  sent_back: 'sent it back',
};

function TrailItem({
  state,
  role,
  title,
  when,
  quote = null,
  action = null,
  sla = null,
}: {
  readonly state: 'done' | 'wait' | 'stop' | 'off';
  readonly role: string;
  readonly title: string;
  readonly when: string | null;
  readonly quote?: string | null;
  readonly action?: ReactNode;
  readonly sla?: SlaSummary | null;
}): ReactNode {
  return (
    <li className={styles.trailItem} data-state={state}>
      <span className={styles.trailDot} aria-hidden>
        {state === 'done' ? <Check size={16} /> : null}
      </span>
      <div className={styles.trailBody}>
        <span className={styles.trailRole}>{role}</span>
        <b className={styles.trailTitle}>{title}</b>
        {when && <span className={styles.meta}>{formatDateTime(when)}</span>}
        {sla && <SlaFigure sla={sla} />}
        {quote && <p className={styles.trailQuote}>“{quote}”</p>}
        {action}
      </div>
    </li>
  );
}

function Clock({ label, sla }: { readonly label: string; readonly sla: SlaSummary | null }): ReactNode {
  // Time to fill can show with Time limits off (it is a Time figure); then it says how long, never "on track" or a limit (bug B9).
  const hasLimits = useModule('sla');
  return (
    <div className={styles.clock}>
      <span className={styles.clockLabel}>{label}</span>
      {sla ? (
        <>
          <SlaFigure sla={sla} showLimit />
          <span className={styles.meta}>
            Started {formatDateTime(sla.startedAt)} · {formatElapsed(sla.elapsedMinutes)} on the calendar,{' '}
            {formatActive(sla.activeMinutes)} of working time{hasLimits ? ` · ${SLA_STATE_LABELS[sla.state].toLowerCase()}` : ''}
          </span>
        </>
      ) : (
        <span className={styles.meta}>{EMPTY}</span>
      )}
    </div>
  );
}

/** Working-time limits belong to Time limits, Time to fill to Time figures (§L28); with both off the card is not drawn. */
function ClocksCard({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  const hasLimits = useModule('sla');
  const hasFigures = useModule('time_metrics');
  if (!hasLimits && !hasFigures) return null;
  return (
    <Card title="Clocks" meta="Elapsed is the calendar; active is working time, less pauses">
      <div className={styles.clocks}>
        {hasLimits && detail.status === 'in_approval' && <Clock label="Waiting on approval" sla={detail.sla} />}
        {hasFigures && <Clock label="Time to fill" sla={detail.timeToFill} />}
        {detail.approvedAt && <p className={styles.meta}>Approved {formatDateTime(detail.approvedAt)}</p>}
      </div>
    </Card>
  );
}

/** The picker opens beside its button (CLAUDE.md §5). */
function RecruiterButton({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="outline" icon={UserPlus} onClick={toggle} aria-haspopup="dialog">
        {detail.recruiterName ? 'Change recruiter' : 'Assign a recruiter'}
      </Button>
      <Popover anchor={anchor} onClose={close} label="Assign a recruiter" width={320}>
        <RecruiterPicker detail={detail} onClose={close} />
      </Popover>
    </>
  );
}

function RecruiterPicker({
  detail,
  onClose,
}: {
  readonly detail: RequisitionDetail;
  readonly onClose: () => void;
}): ReactNode {
  const users = useUsers({ page: 1, pageSize: 200, status: 'account_active' });
  const assign = useAssignRecruiter();
  const showToast = useToasts((state) => state.show);
  const recruiters = (users.data?.rows ?? []).filter((user) => /recruiter/i.test(user.roleLabels));

  return (
    <div className={styles.picker}>
      <Select
        label="Recruiter"
        placeholder="Choose a recruiter"
        value={detail.recruiterUserId}
        options={recruiters.map((user) => ({ value: user.id, label: user.fullName, description: user.roleLabels }))}
        onChange={(recruiterUserId) => {
          void assign
            .mutateAsync({ id: detail.id, version: detail.version, recruiterUserId })
            .then(() => {
              showToast({ tone: 'success', message: `Recruiter assigned to ${detail.reference}` });
              onClose();
            })
            .catch((cause: unknown) =>
              showToast({ tone: 'danger', message: 'The recruiter was not assigned', detail: errorMessage(cause) }),
            );
        }}
      />
      <Button variant="ghost" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: ReactNode }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}

function moneyOf(minor: string | null, currency: string | null): string | null {
  if (minor === null || (currency !== 'BDT' && currency !== 'GBP' && currency !== 'USD')) return null;
  return formatMoney({ amountMinor: BigInt(minor), currency });
}

/** Workflow review 2026-10-29: the budget is a range. */
function budgetOf(detail: RequisitionDetail): string {
  const from = moneyOf(detail.budgetAmountMinor, detail.budgetCurrency);
  const to = moneyOf(detail.budgetMaxMinor, detail.budgetCurrency);
  if (from && to && from !== to) return `${from} – ${to} a month`;
  return from ?? to ?? EMPTY;
}

/** "Earliest 1 Nov, latest 10 Nov 2026". */
function startOf(detail: RequisitionDetail): string {
  const from = detail.targetStartDate ? formatDate(detail.targetStartDate) : null;
  const latest = detail.targetStartLatest ? formatDate(detail.targetStartLatest) : null;
  if (from && latest && from !== latest) return `Earliest ${from}, latest ${latest}`;
  return from ?? latest ?? EMPTY;
}

function typeOf(detail: RequisitionDetail): string {
  if (detail.positionId === null) return 'New position';
  if (detail.hireKind === 'replacement') return `${HIRE_KIND_LABELS.replacement}${detail.replacesName ? `: ${detail.replacesName}` : ''}`;
  if (detail.hireKind === 'additional') return HIRE_KIND_LABELS.additional;
  return detail.isBackfill ? 'Backfill' : EMPTY;
}

/** "Replacement for Imran Kabir · 1 person · start 1–10 Nov · Engineering, TypeSafe". */
function summaryLine(detail: RequisitionDetail): string {
  const parts = [
    detail.hireKind === 'replacement' && detail.replacesName ? `Replacement for ${detail.replacesName}` : null,
    detail.hireKind === 'additional' ? 'Additional hiring' : null,
    `${detail.slotsRequested} ${detail.slotsRequested === 1 ? 'person' : 'people'}`,
    `${detail.departmentName}, ${detail.ventureName}`,
  ];
  return parts.filter((part): part is string => part !== null).join(' · ');
}

const DONE: Readonly<Record<LifecycleAction, (reference: string) => string>> = {
  submit: (ref) => `${ref} sent for approval`,
  revise: (ref) => `${ref} is a draft again`,
  hold: (ref) => `${ref} is on hold`,
  resume: (ref) => `${ref} resumed`,
  cancel: (ref) => `${ref} cancelled`,
};

const SNAPSHOT_LABELS: Readonly<Record<string, string>> = {
  title: 'Title',
  positionCode: 'Position',
  departmentName: 'Department',
  ventureName: 'Venture',
  grade: 'Grade',
  slotsRequested: 'Hires',
  justification: 'Justification',
  priority: 'Priority',
  targetStartDate: 'Target start',
  budgetAmountMinor: 'Budget (minor units)',
  budgetCurrency: 'Currency',
  jevBudgetRequestedMicroUsd: 'Jev budget asked',
  responsibilities: 'Responsibilities',
  achieve: 'What this person will achieve',
  plan_30: 'Onboarding plan, first 30 days',
  plan_90: 'Onboarding plan, first 90 days',
  idealCandidate: 'Ideal candidate',
  managesPeople: 'Manages people',
  location: 'Location',
  workMode: 'Work mode',
};

/** A snapshot value as text: the Jev budget as dollars, the rest as it is. */
function snapshotValueOf(key: string, value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  return key === 'jevBudgetRequestedMicroUsd' && typeof value === 'number' ? jevBudgetLabel(value) : String(value);
}

/** "$5.00 asked" until approved, then "$3.00 approved (asked $5.00)" when the approvers changed it. */
function jevBudgetOf(detail: RequisitionDetail): string {
  return jevBudgetRequestLineOf(detail.jevBudgetRequestedMicroUsd, detail.jevBudgetApprovedMicroUsd) ?? EMPTY;
}
